import { Navigation } from './components'
import {
  ActionIcon,
  Badge,
  Button,
  Input,
  Loader,
  Pagination,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { IconPlus, IconSearch, IconX } from '@tabler/icons-react'
import { useDebounce } from '@/hooks'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { useEffect, useMemo, useState } from 'react'
import { Organization } from '@medplum/fhirtypes'
import { showNotification } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import { Th } from '@/components'
import { formatAddress } from '@medplum/core'
import { modals } from '@mantine/modals'
import type { components } from '@behuman.corp/contracts'
import {
  CreateUpdateLabFacility,
  CreateUpdateOrganization,
  CreateUpdateRadiologyFacility,
  CreateUpdateReferralProvider,
  OrganizationMembers,
} from './components'
import { logError } from '@/utils/errorUtils'

type ListReferralProvidersItemDto = components['schemas']['ListReferralProvidersItemDto']
type ListReferralProvidersResponseDto = components['schemas']['ListReferralProvidersResponseDto']

const renderColumn = (org: Organization, itemCode: string) => {
  switch (itemCode) {
    case 'name':
      return org.name

    case 'type':
      return org.type?.[0]?.coding?.[0]?.display || org.type?.[0]?.text || ''

    case 'address':
      return formatAddress(org.address?.[0])

    default:
      return itemCode
  }
}

const getTelecomValue = (organization: Organization, system: 'phone' | 'fax') => {
  return organization.telecom?.find((item) => item.system === system)?.value || ''
}

function isLabLocation(org: Organization): boolean {
  return (
    org.type?.some((t) =>
      t.coding?.some(
        (c) =>
          c.system === 'https://behuman.health/organization-type' &&
          c.code === 'laboratory-location',
      ),
    ) ?? false
  )
}

type OrgTab = 'organizations' | 'laboratories' | 'radiology-facilities' | 'referral-providers'
const PAGE_SIZE = 50
const ORGANIZATION_FIELDS = [
  { code: 'name', title: 'Name' },
  { code: 'type', title: 'Type' },
  { code: 'address', title: 'Address' },
]

interface LaboratoryContactsFormProps {
  organization: Organization
  onSave: (phone: string, fax: string) => Promise<void>
  onCancel: () => void
}

function LaboratoryContactsForm({ organization, onSave, onCancel }: LaboratoryContactsFormProps) {
  const [phone, setPhone] = useState(getTelecomValue(organization, 'phone'))
  const [fax, setFax] = useState(getTelecomValue(organization, 'fax'))
  const [isSaving, setIsSaving] = useState(false)

  const save = async () => {
    setIsSaving(true)
    try {
      await onSave(phone, fax)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <TextInput
        label="Phone"
        placeholder="Enter phone"
        value={phone}
        onChange={(e) => setPhone(e.currentTarget.value)}
      />
      <TextInput
        label="Fax"
        placeholder="Enter fax"
        value={fax}
        onChange={(e) => setFax(e.currentTarget.value)}
      />
      <div className="flex justify-end gap-2">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={save} loading={isSaving}>
          Save
        </Button>
      </div>
    </div>
  )
}

export function Organizations() {
  const [activeTab, setActiveTab] = useState<OrgTab>('organizations')
  const [searchQuery, setSearchQuery] = useState('')
  const [nameFilter, setNameFilter] = useState('')
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [behumanProjectId, setBehumanProjectId] = useState<string | null>(null)
  const [, setIsProjectLoading] = useState(true)
  const [sortRule, setSortRule] = useState<{ code: string; descending: boolean } | null>(null)
  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  const medplum = useMedplum()
  const middleware = useMiddlewareClient()
  const [referralProviders, setReferralProviders] = useState<ListReferralProvidersItemDto[]>([])
  const [referralFetchKey, setReferralFetchKey] = useState(0)
  const [providerSearch, setProviderSearch] = useState('')
  const debouncedProviderSearch = useDebounce(providerSearch, 500)
  const [pendingLabLocations, setPendingLabLocations] = useState<Organization[]>([])

  useEffect(() => {
    let cancelled = false

    setIsProjectLoading(true)

    medplum
      .search('Project', '_count=1000')
      .then((result) => {
        if (cancelled) {
          return
        }

        const behumanProject =
          result.entry?.find((entry) => entry.resource?.name?.match(/behuman/i))?.resource ||
          result.entry?.find(
            (entry) =>
              entry.resource?.name && !['Super Admin', 'FHIR R4'].includes(entry.resource.name),
          )?.resource

        if (!behumanProject?.id) {
          throw new Error('Failed to locate BeHuman project')
        }

        setBehumanProjectId(behumanProject.id)
      })
      .catch((error) => {
        logError('super', 'Failed to load the BeHuman project', error)
        if (cancelled) {
          return
        }
        setBehumanProjectId(null)
        showNotification({
          title: 'Error',
          message: 'Failed to load the BeHuman project',
          color: 'red',
        })
      })
      .finally(() => {
        if (!cancelled) {
          setIsProjectLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [medplum])

  useEffect(() => {
    setNameFilter(debouncedSearchQuery)
  }, [debouncedSearchQuery])

  // Reset state for Organization/Lab/Radiology tabs. Keeps behumanProjectId as a dep so
  // a project switch (e.g., the BeHuman project id re-resolving) wipes stale rows before
  // the new fetch populates.
  useEffect(() => {
    if (activeTab === 'referral-providers') return
    setCurrentPage(1)
    setOrganizations([])
    setTotalCount(0)
    setSortRule(null)
  }, [activeTab, nameFilter, behumanProjectId])

  // Reset state for the Referral Providers tab. Deliberately omits behumanProjectId —
  // the list endpoint uses the Provider Directory project via its own ClientApp, so a
  // BeHuman project id change is unrelated and must not wipe referral results.
  useEffect(() => {
    if (activeTab !== 'referral-providers') return
    setCurrentPage(1)
    setReferralProviders([])
    setTotalCount(0)
  }, [activeTab, debouncedProviderSearch])

  useEffect(() => {
    if (!behumanProjectId) {
      return
    }
    if (activeTab === 'referral-providers') {
      return // handled by the referral-providers effect below
    }

    let cancelled = false
    setIsLoading(true)

    const queryParts: string[] = [
      `_project=${behumanProjectId}`,
      `_count=${PAGE_SIZE}`,
      `_offset=${(currentPage - 1) * PAGE_SIZE}`,
      '_total=accurate',
      '_sort=name',
    ]

    if (activeTab === 'laboratories') {
      queryParts.push('type=laboratory,laboratory-location')
    } else if (activeTab === 'radiology-facilities') {
      queryParts.push('type=radiology-facility')
    } else {
      queryParts.push(
        'type:not=laboratory',
        'type:not=laboratory-location',
        'type:not=ins',
        'type:not=radiology-facility',
        'type:not=platform',
      )
    }

    if (nameFilter.trim()) {
      queryParts.push(`name:contains=${encodeURIComponent(nameFilter.trim())}`)
    }

    medplum
      .search('Organization', queryParts.join('&'))
      .then((result) => {
        if (cancelled) {
          return
        }

        const orgList = result.entry?.map((entry) => entry.resource as Organization) || []
        setOrganizations(orgList)
        setTotalCount(result.total ?? 0)
      })
      .catch((e) => {
        logError('super', 'Failed to fetch organizations', e)
        if (cancelled) {
          return
        }
        showNotification({
          title: 'Error',
          message: 'Failed to fetch organizations',
          color: 'red',
        })
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [medplum, currentPage, activeTab, nameFilter, behumanProjectId])

  // Fetch all pending lab-locations separately so they appear at the top regardless of pagination
  useEffect(() => {
    if (activeTab !== 'laboratories' || !behumanProjectId) {
      setPendingLabLocations([])
      return
    }

    let cancelled = false
    const query = [
      `type=laboratory-location`,
      `active=false`,
      `_project=${behumanProjectId}`,
      `_count=100`,
      `_sort=name`,
    ]
    if (nameFilter.trim()) query.push(`name:contains=${encodeURIComponent(nameFilter.trim())}`)
    medplum
      .search('Organization', query.join('&'))
      .then((result) => {
        if (cancelled) return
        setPendingLabLocations(result.entry?.map((e) => e.resource as Organization) ?? [])
      })
      .catch((err) => {
        logError('super', 'Failed to fetch pending lab locations', err)
        if (!cancelled) setPendingLabLocations([])
      })

    return () => {
      cancelled = true
    }
  }, [medplum, activeTab, behumanProjectId, nameFilter])

  useEffect(() => {
    if (activeTab !== 'referral-providers') return

    let cancelled = false
    setIsLoading(true)

    const trimmedSearch = debouncedProviderSearch.trim()

    middleware
      .get('/super/list-referral-providers', {
        q: trimmedSearch || undefined,
        _count: PAGE_SIZE,
        _offset: (currentPage - 1) * PAGE_SIZE,
      })
      .then((result: ListReferralProvidersResponseDto) => {
        if (cancelled) return
        setReferralProviders(result.items)
        setTotalCount(result.total)
      })
      .catch((e) => {
        logError('super', 'Failed to list referral providers', e)
        if (cancelled) return
        showNotification({
          title: 'Error',
          message: 'Failed to list referral providers',
          color: 'red',
        })
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [middleware, activeTab, currentPage, debouncedProviderSearch, referralFetchKey])

  const sorted = (itemCode: string) => {
    return sortRule && sortRule.code === itemCode ? sortRule : undefined
  }

  const isSortAvailable = (itemCode: string) => {
    return ['name', 'type', 'address'].includes(itemCode)
  }

  const onSort = (itemCode: string): (() => void) | undefined => {
    if (!isSortAvailable(itemCode)) {
      return undefined
    }
    const existSort = sorted(itemCode)

    if (!existSort) {
      return () => setSortRule({ code: itemCode, descending: true })
    }

    if (existSort.descending) {
      return () => setSortRule({ code: itemCode, descending: false })
    }

    return () => setSortRule(null)
  }

  const createUpdateOrg = (organization?: Organization) => {
    const onSave = (org: Organization) => {
      const newOrgs = organizations.filter((item) => item.id !== org.id)
      newOrgs.unshift(org)
      setOrganizations(newOrgs)
      medplum.invalidateSearches('Organization')
      modals.closeAll()
    }

    modals.open({
      size: 'xl',
      children: (
        <CreateUpdateOrganization
          organization={organization}
          code="prov"
          onSave={onSave}
          onCancel={modals.closeAll}
        />
      ),
    })
  }

  const createUpdateRadiologyFacility = (organization?: Organization) => {
    const onSave = (org: Organization) => {
      const newOrgs = organizations.filter((item) => item.id !== org.id)
      newOrgs.unshift(org)
      setOrganizations(newOrgs)
      medplum.invalidateSearches('Organization')
      modals.closeAll()
    }

    modals.open({
      size: 'xl',
      children: (
        <CreateUpdateRadiologyFacility
          organization={organization}
          onSave={onSave}
          onCancel={modals.closeAll}
        />
      ),
    })
  }

  const createUpdateLabFacility = (organization?: Organization) => {
    const onSave = (org: Organization) => {
      const newOrgs = organizations.filter((item) => item.id !== org.id)
      newOrgs.unshift(org)
      setOrganizations(newOrgs)
      setPendingLabLocations((prev) => prev.filter((item) => item.id !== org.id))
      medplum.invalidateSearches('Organization')
      modals.closeAll()
    }

    modals.open({
      size: 'xl',
      children: (
        <CreateUpdateLabFacility
          organization={organization}
          onSave={onSave}
          onCancel={modals.closeAll}
        />
      ),
    })
  }

  const saveLaboratoryContacts = async (organization: Organization, phone: string, fax: string) => {
    const restTelecom = (organization.telecom || []).filter(
      (item) => item.system !== 'phone' && item.system !== 'fax',
    )

    const updatedOrg = await medplum.updateResource({
      ...organization,
      telecom: [
        ...restTelecom,
        {
          system: 'fax',
          value: fax,
        },
        {
          system: 'phone',
          value: phone,
        },
      ],
    })

    setOrganizations((prev) => prev.map((item) => (item.id === updatedOrg.id ? updatedOrg : item)))
    medplum.invalidateSearches('Organization')
    showNotification({
      title: 'Success',
      message: 'Laboratory contacts updated successfully',
      color: 'green',
    })
    modals.closeAll()
  }

  const openLaboratoryContactsModal = (organization: Organization) => {
    modals.open({
      title: `Manage contacts: ${organization.name || 'Laboratory'}`,
      children: (
        <LaboratoryContactsForm
          organization={organization}
          onSave={(phone, fax) => saveLaboratoryContacts(organization, phone, fax)}
          onCancel={modals.closeAll}
        />
      ),
    })
  }

  const removeResource = async (id: string) => {
    await medplum.deleteResource('Organization', id)
    medplum.invalidateSearches('Organization')
    setOrganizations(organizations.filter((item) => item.id !== id))
    // Pending lab facilities live in a separate array that gets prepended to the
    // Laboratories list, so drop the removed id from there too — otherwise the row
    // lingers until a page reload (PD-894). No-op for radiology (array is empty).
    setPendingLabLocations((prev) => prev.filter((item) => item.id !== id))
  }

  const approveRadiologyFacility = async (org: Organization) => {
    try {
      const updated = await medplum.updateResource({ ...org, active: true })
      setOrganizations((prev) => prev.map((item) => (item.id === updated.id ? updated : item)))
      medplum.invalidateSearches('Organization')
      showNotification({
        title: 'Approved',
        message: `${org.name} has been approved and is now available for orders.`,
        color: 'green',
      })
    } catch (e) {
      logError('super', 'Failed to approve radiology facility', e)
      showNotification({
        title: 'Error',
        message: 'Failed to approve facility',
        color: 'red',
      })
    }
  }

  const approveLabFacility = async (org: Organization) => {
    try {
      const updated = await medplum.updateResource({ ...org, active: true })
      setOrganizations((prev) => {
        const exists = prev.some((item) => item.id === updated.id)
        return exists
          ? prev.map((item) => (item.id === updated.id ? updated : item))
          : [updated, ...prev]
      })
      setPendingLabLocations((prev) => prev.filter((item) => item.id !== updated.id))
      medplum.invalidateSearches('Organization')
      showNotification({
        title: 'Approved',
        message: `${org.name} has been approved and is now available for orders.`,
        color: 'green',
      })
    } catch (e) {
      logError('super', 'Failed to approve lab facility', e)
      showNotification({ title: 'Error', message: 'Failed to approve facility', color: 'red' })
    }
  }

  const deleteReferralProvider = async (roleId: string) => {
    try {
      await middleware.post('/super/delete-referral-provider', { roleId })
      setReferralProviders((prev) => prev.filter((item) => item.roleId !== roleId))
      showNotification({
        title: 'Removed',
        message: 'Referral provider has been removed.',
        color: 'green',
      })
    } catch (e) {
      logError('super', 'Failed to delete referral provider', e)
      showNotification({
        title: 'Error',
        message: 'Failed to remove referral provider',
        color: 'red',
      })
    }
  }

  const approveReferralProvider = async (roleId: string) => {
    try {
      await middleware.post('/super/approve-referral-provider', { roleId })
      setReferralProviders((prev) =>
        prev.map((item) => (item.roleId === roleId ? { ...item, active: true } : item)),
      )
      showNotification({
        title: 'Approved',
        message: 'Referral provider approved and is now available for orders.',
        color: 'green',
      })
    } catch (e) {
      logError('super', 'Failed to approve referral provider', e)
      showNotification({
        title: 'Error',
        message: 'Failed to approve referral provider',
        color: 'red',
      })
    }
  }

  const availFields = ORGANIZATION_FIELDS

  // Detect possible duplicates among radiology facilities
  const duplicateIds = useMemo(() => {
    if (activeTab !== 'radiology-facilities') return new Set<string>()

    const ids = new Set<string>()

    // Group by normalized name
    const byName = new Map<string, Organization[]>()
    for (const org of organizations) {
      const key = (org.name || '').toLowerCase().trim()
      if (!key) continue
      const group = byName.get(key) || []
      group.push(org)
      byName.set(key, group)
    }
    for (const group of byName.values()) {
      if (group.length > 1) {
        for (const org of group) {
          if (org.id) ids.add(org.id)
        }
      }
    }

    // Group by normalized address (street + city + state)
    const byAddr = new Map<string, Organization[]>()
    for (const org of organizations) {
      const addr = org.address?.[0]
      if (!addr?.line?.[0] || !addr.city) continue
      const key = `${(addr.line[0] || '').toLowerCase().trim()}|${(addr.city || '').toLowerCase().trim()}|${(addr.state || '').toLowerCase().trim()}`
      const group = byAddr.get(key) || []
      group.push(org)
      byAddr.set(key, group)
    }
    for (const group of byAddr.values()) {
      if (group.length > 1) {
        for (const org of group) {
          if (org.id) ids.add(org.id)
        }
      }
    }

    return ids
  }, [organizations, activeTab])

  const totalPages = useMemo(() => {
    return Math.ceil(totalCount / PAGE_SIZE)
  }, [totalCount])

  const visibleReferralProviders = useMemo(() => {
    return [...referralProviders].sort((a, b) => {
      const aPending = a.active === false ? 0 : 1
      const bPending = b.active === false ? 0 : 1
      return aPending - bPending
    })
  }, [referralProviders])

  const visibleOrganizations = useMemo(() => {
    let items = [...organizations]

    // For radiology tab, sort duplicates and pending to top
    if (activeTab === 'radiology-facilities') {
      items.sort((a, b) => {
        const aDup = a.id && duplicateIds.has(a.id) ? 0 : 1
        const bDup = b.id && duplicateIds.has(b.id) ? 0 : 1
        if (aDup !== bDup) return aDup - bDup
        const aPending = a.active === false ? 0 : 1
        const bPending = b.active === false ? 0 : 1
        return aPending - bPending
      })
    }

    // For laboratories tab, prepend pending locations fetched separately so they appear
    // at the top of page 1 regardless of where they fall alphabetically in pagination
    if (activeTab === 'laboratories' && pendingLabLocations.length > 0) {
      const pendingIds = new Set(pendingLabLocations.map((o) => o.id))
      items = [...pendingLabLocations, ...items.filter((o) => !pendingIds.has(o.id))]
    }

    if (sortRule) {
      items.sort((a, b) => {
        const valueA = String(renderColumn(a, sortRule.code) || '').toLowerCase()
        const valueB = String(renderColumn(b, sortRule.code) || '').toLowerCase()
        return valueA.localeCompare(valueB)
      })
      if (sortRule.descending) items.reverse()
    }

    return items
  }, [organizations, sortRule, activeTab, duplicateIds, pendingLabLocations])

  return (
    <>
      <Navigation />
      <div className="px-6">
        <div className="sticky top-25 z-10 flex h-[76px] items-center justify-between bg-white">
          <div className="flex w-full items-center gap-4">
            <Title order={2}>Organization list</Title>
            {activeTab === 'referral-providers' ? (
              <Input
                placeholder="Search practitioner name..."
                value={providerSearch}
                onChange={(e) => setProviderSearch(e.currentTarget.value)}
                leftSection={<IconSearch width={16} />}
                rightSection={
                  providerSearch ? (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label="Clear search"
                      onClick={() => setProviderSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : null
                }
                rightSectionPointerEvents={providerSearch ? 'all' : 'none'}
              />
            ) : (
              <Input
                placeholder="Search organization..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.currentTarget.value)}
                leftSection={<IconSearch width={16} />}
                rightSection={
                  searchQuery ? (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label="Clear search"
                      onClick={() => setSearchQuery('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : null
                }
                rightSectionPointerEvents={searchQuery ? 'all' : 'none'}
              />
            )}

            <Tabs
              value={activeTab}
              onChange={(value) => setActiveTab((value as OrgTab) || 'organizations')}
            >
              <Tabs.List>
                <Tabs.Tab value="organizations">Clinics</Tabs.Tab>
                <Tabs.Tab value="laboratories">Laboratories</Tabs.Tab>
                <Tabs.Tab
                  value="radiology-facilities"
                  rightSection={
                    duplicateIds.size > 0 ? (
                      <Badge color="red" size="xs" variant="filled" circle>
                        {duplicateIds.size}
                      </Badge>
                    ) : undefined
                  }
                >
                  Radiology Facilities
                </Tabs.Tab>
                <Tabs.Tab value="referral-providers">Referral Providers</Tabs.Tab>
              </Tabs.List>
            </Tabs>

            {activeTab === 'organizations' && (
              <Button
                onClick={() => createUpdateOrg()}
                variant="subtle"
                leftSection={<IconPlus size={20} />}
              >
                Add Organization
              </Button>
            )}
            {activeTab === 'radiology-facilities' && (
              <Button
                onClick={() => createUpdateRadiologyFacility()}
                variant="subtle"
                leftSection={<IconPlus size={20} />}
              >
                Add Radiology Facility
              </Button>
            )}
            {activeTab === 'laboratories' && (
              <Button
                onClick={() => createUpdateLabFacility()}
                variant="subtle"
                leftSection={<IconPlus size={20} />}
              >
                Add Lab Facility
              </Button>
            )}
            {activeTab === 'referral-providers' && (
              <Button
                onClick={() => {
                  modals.open({
                    size: 'xl',
                    children: (
                      <CreateUpdateReferralProvider
                        onSave={() => {
                          setReferralFetchKey((k) => k + 1)
                          modals.closeAll()
                        }}
                        onCancel={modals.closeAll}
                      />
                    ),
                  })
                }}
                variant="subtle"
                leftSection={<IconPlus size={20} />}
              >
                Add Referral Provider
              </Button>
            )}
          </div>
        </div>

        {isLoading &&
          (activeTab === 'referral-providers'
            ? visibleReferralProviders.length === 0
            : organizations.length === 0) && (
            <div className="flex justify-center py-8">
              <Loader color="blue" />
            </div>
          )}

        <Table>
          <Table.Thead className="bg-[#FAFAFA] text-[#7D7987]">
            <Table.Tr className="sticky top-44 z-10 h-[44px] !bg-[#FAFAFA]">
              {activeTab === 'organizations' ? (
                <>
                  {availFields.map((item) => (
                    <Th
                      className="text-sm"
                      key={item.code}
                      reversed={sorted(item.code)?.descending === true}
                      sorted={!!sorted(item.code)}
                      onSort={onSort(item.code)}
                    >
                      {item.title}
                    </Th>
                  ))}
                  <Th className="text-sm">Actions</Th>
                </>
              ) : activeTab === 'radiology-facilities' ? (
                <>
                  <Th className="text-sm">Name</Th>
                  <Th className="text-sm">Status</Th>
                  <Th className="text-sm">Address</Th>
                  <Th className="text-sm">Fax</Th>
                  <Th className="text-sm">Modalities</Th>
                  <Th className="text-sm">Actions</Th>
                </>
              ) : activeTab === 'referral-providers' ? (
                <>
                  <Th className="text-sm">Practitioner</Th>
                  <Th className="text-sm">Specialty</Th>
                  <Th className="text-sm">Status</Th>
                  <Th className="text-sm">Clinic</Th>
                  <Th className="text-sm">Address</Th>
                  <Th className="text-sm">Phone</Th>
                  <Th className="text-sm">Fax</Th>
                  <Th className="text-sm">Actions</Th>
                </>
              ) : (
                <>
                  <Th className="text-sm">Name</Th>
                  <Th className="text-sm">Status</Th>
                  <Th className="text-sm">Address / Phone</Th>
                  <Th className="text-sm">Fax</Th>
                  <Th className="text-sm">Actions</Th>
                </>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {activeTab === 'referral-providers' ? (
              visibleReferralProviders.length === 0 && !isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={8} className="py-8 text-center text-sm text-gray-500">
                    {debouncedProviderSearch.trim()
                      ? `No providers match "${debouncedProviderSearch.trim()}".`
                      : 'No referral providers yet. Click "Add Referral Provider" to create one.'}
                  </Table.Td>
                </Table.Tr>
              ) : (
                visibleReferralProviders.map((item) => (
                  <Table.Tr key={item.roleId} className="h-[44px]">
                    <Table.Td>
                      {item.practitionerName}
                      {item.npi ? (
                        <span className="ml-2 text-xs text-gray-400">NPI {item.npi}</span>
                      ) : null}
                    </Table.Td>
                    <Table.Td>{item.specialtyLabel || item.specialtyCode || '—'}</Table.Td>
                    <Table.Td>
                      {item.active === false ? (
                        <Badge color="orange" size="sm" variant="light">
                          Pending
                        </Badge>
                      ) : (
                        <Badge color="green" size="sm" variant="light">
                          Approved
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>{item.organizationName || '—'}</Table.Td>
                    <Table.Td>
                      {[item.address, item.city, item.state, item.zip].filter(Boolean).join(', ') ||
                        '—'}
                    </Table.Td>
                    <Table.Td>{item.phone || '—'}</Table.Td>
                    <Table.Td>{item.fax || '—'}</Table.Td>
                    <Table.Td>
                      <div className="flex gap-2">
                        {item.active === false && (
                          <Button
                            color="green"
                            variant="outline"
                            size="xs"
                            onClick={() => approveReferralProvider(item.roleId)}
                          >
                            Approve
                          </Button>
                        )}
                        <Button
                          color="red"
                          variant="outline"
                          size="xs"
                          onClick={() =>
                            modals.openConfirmModal({
                              title: 'Remove referral provider?',
                              children: (
                                <Text size="sm">
                                  This will permanently remove {item.practitionerName} from the
                                  provider directory.
                                </Text>
                              ),
                              confirmProps: { color: 'red' },
                              labels: { confirm: 'Remove', cancel: 'Cancel' },
                              onConfirm: () => {
                                void deleteReferralProvider(item.roleId)
                              },
                            })
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    </Table.Td>
                  </Table.Tr>
                ))
              )
            ) : (
              visibleOrganizations.map((org) => (
                <Table.Tr
                  key={org.id}
                  onClick={() =>
                    activeTab === 'organizations'
                      ? createUpdateOrg(org)
                      : activeTab === 'radiology-facilities'
                        ? createUpdateRadiologyFacility(org)
                        : activeTab === 'laboratories' && isLabLocation(org)
                          ? createUpdateLabFacility(org)
                          : openLaboratoryContactsModal(org)
                  }
                  className="h-[44px] cursor-pointer hover:opacity-85"
                >
                  {activeTab === 'organizations' ? (
                    availFields.map((item) => (
                      <Table.Td key={item.code}>{renderColumn(org, item.code)}</Table.Td>
                    ))
                  ) : activeTab === 'radiology-facilities' ? (
                    <>
                      <Table.Td>
                        <div className="flex items-center gap-2">
                          {org.name}
                          {org.id && duplicateIds.has(org.id) && (
                            <Badge color="red" size="xs" variant="light">
                              Possible Duplicate
                            </Badge>
                          )}
                        </div>
                      </Table.Td>
                      <Table.Td>
                        {org.active === false ? (
                          <Badge color="orange" size="sm" variant="light">
                            Pending
                          </Badge>
                        ) : (
                          <Badge color="green" size="sm" variant="light">
                            Approved
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>{formatAddress(org.address?.[0])}</Table.Td>
                      <Table.Td>
                        {getTelecomValue(org, 'fax') || (
                          <span className="text-red-400">No fax</span>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {(() => {
                          const raw = org.extension?.find(
                            (e) =>
                              e.url ===
                              'https://behuman.health/fhir/StructureDefinition/radiology-modalities',
                          )?.valueString
                          if (!raw) return '—'
                          try {
                            const arr = JSON.parse(raw) as string[]
                            return `${arr.length} modalities`
                          } catch {
                            return '—'
                          }
                        })()}
                      </Table.Td>
                    </>
                  ) : (
                    <>
                      <Table.Td>{org.name}</Table.Td>
                      <Table.Td>
                        {isLabLocation(org) ? (
                          org.active === false ? (
                            <Badge color="orange" size="sm" variant="light">
                              Pending
                            </Badge>
                          ) : (
                            <Badge color="green" size="sm" variant="light">
                              Approved
                            </Badge>
                          )
                        ) : null}
                      </Table.Td>
                      <Table.Td>
                        {isLabLocation(org)
                          ? formatAddress(org.address?.[0])
                          : getTelecomValue(org, 'phone')}
                      </Table.Td>
                      <Table.Td>
                        {getTelecomValue(org, 'fax') ||
                          (isLabLocation(org) ? (
                            <span className="text-red-400">No fax</span>
                          ) : null)}
                      </Table.Td>
                    </>
                  )}
                  <Table.Td>
                    <div className="flex gap-2">
                      {activeTab === 'organizations' ? (
                        <>
                          <Button
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation()
                              modals.open({
                                size: '1200px',
                                children: (
                                  <OrganizationMembers
                                    organizationId={org.id!}
                                    organizationName={org.name}
                                  />
                                ),
                              })
                            }}
                          >
                            Open Members
                          </Button>
                          <Button
                            color="red"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation()
                              modals.openConfirmModal({
                                title: 'Are you sure you want to remove this resource?',
                                confirmProps: { color: 'red' },
                                onConfirm: () => {
                                  void removeResource(org.id!)
                                },
                              })
                            }}
                          >
                            Remove
                          </Button>
                        </>
                      ) : activeTab === 'radiology-facilities' ? (
                        <>
                          {org.active === false && (
                            <Button
                              color="green"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation()
                                void approveRadiologyFacility(org)
                              }}
                            >
                              Approve
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation()
                              createUpdateRadiologyFacility(org)
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            color="red"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation()
                              modals.openConfirmModal({
                                title: 'Are you sure you want to remove this facility?',
                                confirmProps: { color: 'red' },
                                onConfirm: () => {
                                  void removeResource(org.id!)
                                },
                              })
                            }}
                          >
                            Remove
                          </Button>
                        </>
                      ) : isLabLocation(org) ? (
                        <>
                          {org.active === false && (
                            <Button
                              color="green"
                              variant="outline"
                              size="xs"
                              onClick={(e) => {
                                e.stopPropagation()
                                void approveLabFacility(org)
                              }}
                            >
                              Approve
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="xs"
                            onClick={(e) => {
                              e.stopPropagation()
                              createUpdateLabFacility(org)
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            color="red"
                            variant="outline"
                            size="xs"
                            onClick={(e) => {
                              e.stopPropagation()
                              modals.openConfirmModal({
                                title: 'Remove lab facility?',
                                confirmProps: { color: 'red' },
                                onConfirm: () => {
                                  void removeResource(org.id!)
                                },
                              })
                            }}
                          >
                            Remove
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation()
                            openLaboratoryContactsModal(org)
                          }}
                        >
                          Manage contacts
                        </Button>
                      )}
                    </div>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>

        {totalPages > 1 && (
          <div className="flex justify-center py-4">
            <Pagination
              total={totalPages}
              value={currentPage}
              onChange={setCurrentPage}
              withEdges
            />
          </div>
        )}
      </div>
    </>
  )
}
